import {
  getEnvironments as getEnvironmentsService,
  putEnvironments,
} from '@whistler-ui/portal/services/environmentsService';
import { Environment } from '@whistler-ui/portal/types/environments';
import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { IS_PRODUCTION_ENV, PRODUCTION, SETTINGS, TESTING } from '../constants';

export interface EntitlementsState {
  inbound: {
    cert: string;
    cert_expiration_date: string | null;
  };
  loading: boolean;
  isLiveView?: boolean;
  current: Environment | null;
  list: Environment[];
  loadingSelectedUseCases: boolean;
  error: string;
}

export const initialState: EntitlementsState = {
  inbound: {
    cert: '',
    cert_expiration_date: null,
  },
  loading: false,
  isLiveView: undefined,
  current: null,
  list: [],
  loadingSelectedUseCases: true,
  error: '',
};

interface UpdateEnvironmentPayload {
  silent?: boolean;
  payload?: Partial<Environment>;
}

interface GetEnvironmentsPayload {
  silent?: boolean;
  isValidProductionUpgrade?: boolean;
}

const findCurrentEnvironment = (
  environments: Environment[],
  isLiveView?: boolean,
) => {
  return (
    environments.find(env => {
      return isLiveView ? env.type === PRODUCTION : env.type === TESTING;
    }) || null
  );
};

export type EntitlementsStore = EntitlementsState & {
  toggleEnvironment: (isLiveViewEnvironment: boolean) => void;
  updateEnvironmentNoCert: (
    payload?: UpdateEnvironmentPayload,
  ) => Promise<Environment | null>;
  getEnvironments: (
    payload?: GetEnvironmentsPayload,
  ) => Promise<Environment[] | null>;
  resetStore: () => void;
};

export const useEntitlementsStore = createWithEqualityFn<EntitlementsStore>()(
  devtools(
    set => ({
      ...initialState,
      toggleEnvironment: isLiveViewEnvironment => {
        sessionStorage.setItem(
          IS_PRODUCTION_ENV,
          String(isLiveViewEnvironment),
        );

        set(
          state => ({
            isLiveView: isLiveViewEnvironment,
            current: findCurrentEnvironment(state.list, isLiveViewEnvironment),
          }),
          false,
          'entitlementsStore/toggleEnvironment',
        );
      },

      updateEnvironmentNoCert: async ({
        silent = true,
        payload,
      }: UpdateEnvironmentPayload = {}) => {
        if (!payload) return null;

        try {
          const response = await putEnvironments(payload, {
            withCert: false,
          });

          set(
            state => ({
              list: state.list.map(env =>
                env.id === response.id ? response : env,
              ),
              current: response,
            }),
            false,
            'entitlementsStore/updateEnvironmentNoCert',
          );

          return response;
        } catch (error: any) {
          if (silent) return null;
          throw error;
        }
      },

      getEnvironments: async ({
        silent = true,
      }: GetEnvironmentsPayload = {}) => {
        try {
          set(
            () => ({ loading: true }),
            false,
            'entitlementsStore/setLoadingEnvironments',
          );

          const response = await getEnvironmentsService();

          set(
            () => {
              const isLiveView =
                sessionStorage.getItem(IS_PRODUCTION_ENV) === 'true';

              return {
                list: response,
                isLiveView,
                current: findCurrentEnvironment(response, isLiveView),
                loading: false,
              };
            },
            false,
            'entitlementsStore/setEnvironments',
          );

          // The second loading-false update is technically redundant, since
          // setEnvironments already sets loading: false — kept to strictly
          // match the original reducer flow.
          set(
            () => ({ loading: false }),
            false,
            'entitlementsStore/setLoadingEnvironments',
          );

          return response;
        } catch (error: any) {
          set(
            () => ({ loading: false }),
            false,
            'entitlementsStore/setLoadingEnvironments',
          );

          if (silent) {
            return null;
          }

          throw error;
        }
      },

      resetStore: () => {
        set(() => initialState, false, 'entitlementsStore/resetStore');
      },
    }),
    {
      name: 'entitlementsStore',
      enabled: SETTINGS.IS_DEV,
    },
  ),
  shallow,
);
